import { DateTime } from 'luxon';

import {
  mkSpendingWatchList,
  type SpendingWatchlist,
  type SpendingWatchlistProps,
  type SpendingWatchlistRequest,
  type SpendingWatchlistResponse,
} from './spendingWatchlistTypes';

/**
 * startDate/endDate are calendar dates — the day the user picked, with no time of day and
 * no zone — so the app works with a bare `yyyy-MM-dd` throughout. Read the date off the
 * front of the string rather than parsing it: parsing re-interprets the value as an instant
 * in the viewer's zone, and `2026-08-03T00:00:00Z` read that way is Aug 2 anywhere west of
 * UTC. That off-by-one is what this pair exists to prevent.
 */
const toCalendarDate = (date: string | null | undefined): string | null | undefined => {
  if (typeof date !== 'string') {
    return date;
  }
  return /^\d{4}-\d{2}-\d{2}/.exec(date)?.[0] ?? date;
};

/**
 * The API models these as ZonedDateTime, so a bare `yyyy-MM-dd` is rejected with
 * QCS-0400-2 and we have to widen them on the way out.
 *
 * Widen to UTC midnight, never local midnight. Local midnight carries the sender's offset
 * (`2026-08-03T00:00:00-07:00`); once that is normalized to an instant, readers on the
 * other side of the offset get the neighbouring day, and re-saving persists the shift.
 * Taking the calendar date first also means it doesn't matter which shape the value
 * arrives in — bare date, local-offset datetime (web's ExtendDialog builds one), or a
 * value the server already returned — they all widen to the same instant.
 *
 * Invalid input is passed through untouched so the server error surfaces rather than
 * being silently swallowed into a null.
 */
const toZonedDateTime = (date: string | null | undefined): string | null | undefined => {
  if (!date) {
    return date;
  }
  const parsed = DateTime.fromISO(toCalendarDate(date) ?? '', { zone: 'utc' });
  return parsed.isValid ? parsed.toISO() : date;
};

export function transformSpendingWatchListToRequestData(
  spendingWatchList: SpendingWatchlistProps,
): SpendingWatchlistRequest {
  return {
    id: spendingWatchList.id,
    clientId: spendingWatchList.clientId,
    isDeleted: spendingWatchList.isDeleted,
    name: spendingWatchList.name,
    filterId: spendingWatchList.filterId,
    targetAmount: spendingWatchList.targetAmount,
    resetTargetAmount: spendingWatchList.resetTargetAmount,
    emoji: spendingWatchList.emoji,
    period: spendingWatchList.period,
    startDate: toZonedDateTime(spendingWatchList.startDate),
    endDate: toZonedDateTime(spendingWatchList.endDate),
  };
}

export function transformResponseToSpendingWatchLists(
  response?: SpendingWatchlistResponse | null,
): SpendingWatchlist[] | undefined {
  return response?.data?.resources?.map((data) =>
    mkSpendingWatchList({
      id: data.id,
      clientId: data.clientId,
      isDeleted: data.isDeleted,
      name: data.name,
      filterId: data.filterId,
      thisMonth: data.thisMonth,
      yearToDate: data.yearToDate,
      monthlyTrend: data.monthlyTrend,
      numMonthlyTrendMonths: data.numMonthlyTrendMonths,
      monthSpendingProjection: data.monthSpendingProjection,
      targetAmount: data.targetAmount,
      emoji: data.emoji,
      createdAt: data.createdAt,
      period: data.period,
      startDate: toCalendarDate(data.startDate),
      endDate: toCalendarDate(data.endDate),
      spentSoFar: data.spentSoFar,
    }),
  );
}
